import { useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Calculator, AlertTriangle, CheckCircle, ShieldAlert, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LEVELS = ["Priority", "Express", "Standard"];
const ORDER_TYPES = ["B2B", "B2C", "C2C"];

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PartnerBookings() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const partnerId = user?.partner_id || user?.id;
  const partnerType = user?.partner_type || "NETWORK";

  const [form, setForm] = useState({
    customer_name: "",
    origin_pincode: "",
    destination_pincode: "",
    actual_weight_kg: "",
    length_cm: "",
    width_cm: "",
    height_cm: "",
    service_level: "Standard",
    order_type: "B2C",
    invoice_value: "",
    notes: "",
  });

  const [breakdown, setBreakdown] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overridePrice, setOverridePrice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fmInfo, setFmInfo] = useState(null); // {distance_km, tier, rate_per_kg, min_charge}
  const [lmInfo, setLmInfo] = useState(null);
  const [fmLoading, setFmLoading] = useState(false);
  const [lmLoading, setLmLoading] = useState(false);

  // Gate EXECUTION partners (AFTER all hooks to respect rules-of-hooks)
  if (partnerType === "EXECUTION") {
    return (
      <DashboardLayout portalColor="green">
        <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="access-denied">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500 max-w-md">
            EXECUTION partners cannot create bookings. This feature is only available to
            NETWORK and REVENUE partners.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchDistance = async (to_pincode, setInfo, setLoading) => {
    const hub = user?.hub_pincode;
    if (!hub || !to_pincode || to_pincode.length !== 6) {
      setInfo(null);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/v2/partners/distance?from_pincode=${hub}&to_pincode=${to_pincode}`,
        { headers }
      );
      if (r.ok) {
        const d = await r.json();
        if (d.error || d.distance_km == null) {
          setInfo({ error: d.error || "calculating..." });
        } else {
          setInfo(d);
        }
      } else {
        setInfo({ error: "calculating..." });
      }
    } catch {
      setInfo({ error: "calculating..." });
    }
    setLoading(false);
  };

  const onOriginBlur = () => {
    fetchDistance(form.origin_pincode, setFmInfo, setFmLoading);
    calculateRate();
  };
  const onDestBlur = () => {
    fetchDistance(form.destination_pincode, setLmInfo, setLmLoading);
    calculateRate();
  };

  const canCalculate =
    form.origin_pincode.length === 6 &&
    form.destination_pincode.length === 6 &&
    parseFloat(form.actual_weight_kg) > 0;

  const calculateRate = async () => {
    if (!canCalculate) return;
    setCalcLoading(true);
    setBreakdown(null);
    try {
      const res = await fetch(`${API}/api/v2/rate/own-network/calculate`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          origin_pincode: form.origin_pincode,
          destination_pincode: form.destination_pincode,
          actual_weight_kg: parseFloat(form.actual_weight_kg),
          length_cm: parseFloat(form.length_cm) || 30,
          width_cm: parseFloat(form.width_cm) || 20,
          height_cm: parseFloat(form.height_cm) || 15,
          service_level: form.service_level,
          order_type: form.order_type,
          invoice_value: parseFloat(form.invoice_value) || 0,
          fm_distance_km: fmInfo?.distance_km || null,
          lm_distance_km: lmInfo?.distance_km || null,
        }),
      });
      if (res.ok) {
        setBreakdown(await res.json());
      } else {
        const err = await res.json().catch(() => ({ detail: "Calculation failed" }));
        toast.error(err.detail || "Rate calculation failed");
      }
    } catch (e) {
      toast.error("Calc error: " + e.message);
    }
    setCalcLoading(false);
  };

  const systemPrice = breakdown?.final_amount || breakdown?.system_price || 0;
  const overridePriceNum = parseFloat(overridePrice) || 0;
  const inflation = systemPrice > 0 ? ((overridePriceNum - systemPrice) / systemPrice) * 100 : 0;
  const minAllowed = systemPrice * 1.25;

  let overrideStatus = "ok"; // "ok" | "warn" | "block"
  let overrideMsg = "";
  if (overrideEnabled && overridePriceNum > 0 && systemPrice > 0) {
    if (Math.abs(overridePriceNum - systemPrice) < 0.01) {
      overrideStatus = "ok";
      overrideMsg = "Same as system price";
    } else if (overridePriceNum >= minAllowed) {
      overrideStatus = "ok";
      overrideMsg = `Inflation: ${inflation.toFixed(1)}% above system price`;
    } else if (overridePriceNum > systemPrice) {
      overrideStatus = "block";
      overrideMsg = `Minimum price inflation must be 25%. Enter ${fmt(minAllowed)} or higher (25% above ${fmt(systemPrice)})`;
    } else {
      overrideStatus = "block";
      overrideMsg = `Override price must equal system price or be at least 25% higher.`;
    }
  }

  const canSubmit = breakdown && (!overrideEnabled || overrideStatus === "ok") && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const finalPrice = overrideEnabled ? overridePriceNum : systemPrice;
    try {
      const payload = {
        partner_id: partnerId,
        booking_by: "partner",
        customer_name: form.customer_name,
        account_type: "domestic",
        shipping_mode: form.service_level.toLowerCase(),
        payment_mode: "prepaid",
        consignment_type: "non-document",
        service_level: form.service_level.toUpperCase(),
        order_type: form.order_type,
        consignment_value: parseFloat(form.invoice_value) || 0,
        origin: { name: form.customer_name, pincode: form.origin_pincode },
        destination: { name: form.customer_name, pincode: form.destination_pincode },
        package: {
          weight: parseFloat(form.actual_weight_kg),
          type: "non-document",
          description: form.notes || "Partner booking",
        },
        chargeable_weight: breakdown.chargeable_weight_kg || parseFloat(form.actual_weight_kg),
        volumetric_weight: breakdown.volumetric_weight_kg || 0,
        boxes: [{
          qty: 1,
          l: parseFloat(form.length_cm) || 30,
          b: parseFloat(form.width_cm) || 20,
          h: parseFloat(form.height_cm) || 15,
        }],
        price_override: overrideEnabled ? finalPrice : null,
        notes: form.notes,
      };
      const res = await fetch(`${API}/api/shipments`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Booking created! AWB: ${d.tracking_id || d.awb || d.id}`);
        // Reset
        setForm({
          customer_name: "", origin_pincode: "", destination_pincode: "",
          actual_weight_kg: "", length_cm: "", width_cm: "", height_cm: "",
          service_level: "Standard", order_type: "B2C", invoice_value: "", notes: "",
        });
        setBreakdown(null);
        setOverrideEnabled(false);
        setOverridePrice("");
      } else {
        const err = await res.json().catch(() => ({ detail: "Booking failed" }));
        toast.error(err.detail || "Booking failed");
      }
    } catch (e) { toast.error("Error: " + e.message); }
    setSubmitting(false);
  };

  return (
    <DashboardLayout portalColor="green">
      <div className="space-y-5 max-w-5xl" data-testid="partner-bookings">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">New Booking</h1>
            <p className="text-sm text-slate-500">Create a shipment on behalf of your customer</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">{partnerType}</Badge>
        </div>

        {/* FORM */}
        <div className="bg-white border rounded-lg p-5 space-y-4">
          <Field label="Customer Name">
            <Input value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)}
                   placeholder="Customer full name" data-testid="input-customer-name" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Origin Pincode">
              <Input value={form.origin_pincode} maxLength={6} onBlur={onOriginBlur}
                     onChange={(e) => setField("origin_pincode", e.target.value.replace(/\D/g, ""))}
                     placeholder="6-digit pincode" data-testid="input-origin-pincode" />
              <FmLmHint label="FM" info={fmInfo} loading={fmLoading} />
            </Field>
            <Field label="Destination Pincode">
              <Input value={form.destination_pincode} maxLength={6} onBlur={onDestBlur}
                     onChange={(e) => setField("destination_pincode", e.target.value.replace(/\D/g, ""))}
                     placeholder="6-digit pincode" data-testid="input-dest-pincode" />
              <FmLmHint label="LM" info={lmInfo} loading={lmLoading} />
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Actual Weight (kg)">
              <Input type="number" value={form.actual_weight_kg} onBlur={calculateRate}
                     onChange={(e) => setField("actual_weight_kg", e.target.value)}
                     placeholder="0.0" data-testid="input-weight" />
            </Field>
            <Field label="Length (cm)">
              <Input type="number" value={form.length_cm} onBlur={calculateRate}
                     onChange={(e) => setField("length_cm", e.target.value)}
                     placeholder="0" data-testid="input-length" />
            </Field>
            <Field label="Width (cm)">
              <Input type="number" value={form.width_cm} onBlur={calculateRate}
                     onChange={(e) => setField("width_cm", e.target.value)}
                     placeholder="0" data-testid="input-width" />
            </Field>
            <Field label="Height (cm)">
              <Input type="number" value={form.height_cm} onBlur={calculateRate}
                     onChange={(e) => setField("height_cm", e.target.value)}
                     placeholder="0" data-testid="input-height" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Service Level">
              <select value={form.service_level} onChange={(e) => setField("service_level", e.target.value)}
                      className="w-full border border-slate-300 rounded h-10 px-3 text-sm"
                      data-testid="select-service-level">
                {SERVICE_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Order Type">
              <select value={form.order_type} onChange={(e) => setField("order_type", e.target.value)}
                      className="w-full border border-slate-300 rounded h-10 px-3 text-sm"
                      data-testid="select-order-type">
                {ORDER_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Invoice Value (₹)">
              <Input type="number" value={form.invoice_value} onBlur={calculateRate}
                     onChange={(e) => setField("invoice_value", e.target.value)}
                     placeholder="For insurance" data-testid="input-invoice" />
            </Field>
          </div>

          <Field label="Notes (optional)">
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                      rows={2} placeholder="Any special instructions..." data-testid="input-notes" />
          </Field>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={calculateRate}
                    disabled={!canCalculate || calcLoading} data-testid="calculate-btn">
              {calcLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Calculator className="w-4 h-4 mr-1.5" />}
              Calculate Rate
            </Button>
            <span className="text-xs text-slate-400">
              Auto-calculates on field blur when pincodes + weight are filled.
            </span>
          </div>
        </div>

        {/* RATE BREAKDOWN */}
        {breakdown && (
          <div className="bg-blue-50/60 border-blue-200 border rounded-lg p-5" data-testid="rate-breakdown">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Rate Breakdown</h3>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <BreakdownRow label="Chargeable Weight" value={`${breakdown.chargeable_weight_kg || 0} kg`} />
              <BreakdownRow label="Base Charge" value={fmt(breakdown.base_charge)} />
              <BreakdownRow label="FM Charge" value={fmt(breakdown.fm_charge)} />
              <BreakdownRow label="LM Charge" value={fmt(breakdown.lm_charge)} />
              <BreakdownRow label="Fuel Surcharge" value={fmt(breakdown.fuel_surcharge)} />
              <BreakdownRow label="Insurance" value={fmt(breakdown.insurance)} />
              <BreakdownRow label="Margin" value={fmt(breakdown.margin)} />
              <BreakdownRow label="Total Before GST" value={fmt(breakdown.amount_before_gst || breakdown.subtotal)} />
              <BreakdownRow label="GST 18%" value={fmt(breakdown.gst)} />
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">SYSTEM PRICE</span>
              <span className="text-xl font-bold text-blue-700" data-testid="system-price">
                {fmt(systemPrice)}
              </span>
            </div>
          </div>
        )}

        {/* PRICE OVERRIDE */}
        {breakdown && (
          <div className="bg-white border rounded-lg p-5 space-y-3" data-testid="price-override-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={overrideEnabled}
                     onChange={(e) => setOverrideEnabled(e.target.checked)}
                     className="accent-emerald-600"
                     data-testid="override-toggle" />
              <span className="font-semibold text-sm">Override System Price</span>
            </label>
            {overrideEnabled && (
              <div>
                <Input type="number" value={overridePrice}
                       onChange={(e) => setOverridePrice(e.target.value)}
                       placeholder="Enter override price" data-testid="override-price-input" />
                {overridePrice && (
                  <div className={`mt-2 px-3 py-2 rounded text-xs flex items-center gap-2 ${
                    overrideStatus === "block"
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  }`} data-testid="override-validation">
                    {overrideStatus === "block" ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
                    <span>{overrideMsg}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SUBMIT */}
        <div className="flex justify-end">
          <Button onClick={onSubmit} disabled={!canSubmit}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-booking-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Create Booking
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function BreakdownRow({ label, value }) {
  return (
    <>
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </>
  );
}

function FmLmHint({ label, info, loading }) {
  if (!info && !loading) return null;
  if (loading) {
    return <p className="mt-1 text-[11px] text-slate-400">Distance: calculating...</p>;
  }
  if (info?.error) {
    return <p className="mt-1 text-[11px] text-slate-400">Distance: {info.error}</p>;
  }
  return (
    <p className="mt-1 text-[11px] text-slate-600" data-testid={`hint-${label.toLowerCase()}`}>
      <span className="font-semibold text-emerald-700">{label} Distance:</span>{" "}
      ~{info.distance_km} km
      <span className="text-slate-400">
        {" "}(Tier: {info.tier} | Rate: \u20B9{info.rate_per_kg}/kg | Min: \u20B9{info.min_charge})
      </span>
    </p>
  );
}
